import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '@/components/icons';
import { useMemberCountryRegions } from '@/lib/api/hooks/region';
import { useAllMemberCountries, useUpdateMemberCountryStatus } from '@/lib/api/hooks/memberCountry';
import type { MemberCountryRegion, MemberCountry } from '@/lib/api/types';
import {  ActionDropdown, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { Eye, Pencil, ChevronRight, ChevronDown } from 'lucide-react';
import { showToast } from '@/lib/toast';
import MemberCountriesStats from '@/pages/about/member-countries/MemberCountriesStats';
import AfricaSilhouetteIcon from '@/assets/icons/AfricaSilhouetteIcon';

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface RegionColor { bg: string; text: string }
const getRegionColor = (regionName: string): RegionColor => {
  const n = regionName.toLowerCase();
  if (n.includes('north'))   return { bg: 'bg-red-300',   text: 'text-rose-600'   };
  if (n.includes('east'))    return { bg: 'bg-amber-100',  text: 'text-amber-600'  };
  if (n.includes('west'))    return { bg: 'bg-green-100',  text: 'text-green-700'  };
  if (n.includes('central')) return { bg: 'bg-blue-100',   text: 'text-blue-600'   };
  if (n.includes('south'))   return { bg: 'bg-purple-100', text: 'text-purple-600' };
  return { bg: 'bg-gray-100', text: 'text-gray-600' };
};

// ─── RegionRow ────────────────────────────────────────────────────────────────

interface RegionRowProps {
  region: MemberCountryRegion;
  countries: MemberCountry[];
  isExpanded: boolean;
  onToggle: () => void;
}

const RegionRow: React.FC<RegionRowProps> = ({ region, countries, isExpanded, onToggle }) => {
  const navigate = useNavigate();
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<MemberCountry | null>(null);
  const updateStatusMutation = useUpdateMemberCountryStatus();

  const publishedCountries = useMemo(() => countries.filter(c => c.status === 'PUBLISH'), [countries]);
  const MAX_CHIPS = 1;
  const visibleChips = publishedCountries.slice(0, MAX_CHIPS);
  const overflowCount = publishedCountries.length - MAX_CHIPS;
  const color = getRegionColor(region.regionName);
  const navState = { regionName: region.regionName, regionId: region.regionId, regionTag: region.regionTag };

  const handleConfirmStatusUpdate = () => {
    if (!selectedCountry) return;
    const newStatus = selectedCountry.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';
    updateStatusMutation.mutate(
      { memberCountryId: selectedCountry.memberCountryId, data: { status: newStatus } },
      {
        onSuccess: () => {
          showToast.success(newStatus === 'PUBLISH' ? 'Country published!' : 'Country unpublished!');
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedCountry(null);
        },
        onError: (err: Error) => showToast.error('Failed: ' + (err.message || 'Unknown error')),
      }
    );
  };

  const getDropdownActions = (country: MemberCountry): DropdownAction[] => [
    {
      label: country.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
      onClick: (e) => {
        e.stopPropagation();
        setSelectedCountry(country);
        if (country.status === 'PUBLISH') setUnpublishModalOpen(true);
        else setPublishModalOpen(true);
      },
      disabled: updateStatusMutation.isPending,
    },
  ];

  return (
    <div className="border border-gray-200 bg-white rounded-lg overflow-hidden">
      {/* Header row */}
      <div
        role="button"
        className="grid grid-cols-[5fr_7fr] 2xl:grid-cols-[7fr_5fr] items-center gap-4 p-4 sm:p-5 cursor-pointer select-none hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className='flex items-center gap-3 w-full'>
           <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded ${color.bg}`}>
          <AfricaSilhouetteIcon className={`h-6 w-6 ${color.text}`} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{region.regionName}</p>
          <p className="text-xs text-gray-500 whitespace-nowrap mt-0.5">
            Last updated {new Date(region.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        </div>

        </div>

        <div className='flex items-center overflow-hidden gap-4'>
       
        <div className=" text-center">
          <p className="text-lg font-semibold text-gray-900 leading-tight">{region.numberOfCountries}</p>
          <p className="text-xs text-gray-500">{region.numberOfCountries === 1 ? 'Country' : 'Countries'}</p>
        </div>

        <div className="hidden overflow-hidden flex-1 sm:flex items-center gap-2">
          {visibleChips.map((c) => (
            <div
              key={c.memberCountryId}
              className="flex items-center gap-1.5 rounded-md border overflow-hidden border-gray-200 bg-white px-2.5 py-1 text-sm text-gray-700"
            >
              <img src={c.flagUrl} alt={c.countryName} className="h-3.5 w-5 shrink-0 rounded-sm object-cover" />
              <span className="text-ellipsis truncate overflow-hidden whitespace-nowrap ">
              {c.countryName}

              </span>
            </div>
          ))}
          {overflowCount > 0 && (
            <span className="text-sm font-medium text-gray-500">+{overflowCount}</span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            title="View region details"
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/member-countries/${region.regionId}`, { state: navState }); }}
            className="grid grid-cols-[auto_1fr] items-center gap-1.5 rounded-md  bg-[#047857] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#065f46] active:scale-95 transition-all"
          >
            <Eye className="h-3.5 w-3.5" />
            <span>View Region</span>
          </button>
          <div className="w-px h-5 bg-gray-200" />
          <div className="p-1">
            {isExpanded
              ? <ChevronDown className="h-5 w-5 text-gray-400" />
              : <ChevronRight className="h-5 w-5 text-gray-400" />}
          </div>
        </div>
        </div>
      </div>

      {/* Expanded section */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-4 pb-4 pt-3">
          <p className="mb-3 text-sm font-medium text-gray-600">Countries in {region.regionName}</p>

          {countries.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">No countries in this region yet.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Country</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Has Space Agency?</th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Status</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Last Updated</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {countries.map((country) => (
                    <tr key={country.memberCountryId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <img src={country.flagUrl} alt={country.countryName} className="h-4 w-6 shrink-0 rounded-sm object-cover" />
                          <span className="font-medium text-gray-900">{country.countryName}</span>
                        </div>
                      </td>
                        <td className="px-4 py-3 text-gray-500">
                        {country.hasSpaceProgramme ? 'Yes' : 'No'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          country.status === 'PUBLISH' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {country.status === 'PUBLISH' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(country.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="View"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/member-countries/${region.regionId}/${country.memberCountryId}`, { state: navState }); }}
                            className="rounded p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/member-countries/${region.regionId}/edit/${country.memberCountryId}`, { state: { ...navState, country } }); }}
                            className="rounded p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <div onClick={(e) => e.stopPropagation()}>
                            <ActionDropdown id={country.memberCountryId} actions={getDropdownActions(country)} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* This section is hidden for now. All countries are populated automatically so no need to add manually */}
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3" style={{display: "none"}}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/member-countries/${region.regionId}/add`, { state: navState }); }}
                  className="text-sm font-medium text-[#047857] hover:underline"
                >
                  Add a country to {region.regionName}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/member-countries/${region.regionId}/add`, { state: navState }); }}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  + Add Country
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedCountry && (
        <>
          <PublishModal
            isOpen={publishModalOpen}
            onClose={() => { setPublishModalOpen(false); setSelectedCountry(null); }}
            onConfirm={handleConfirmStatusUpdate}
            title="Publish Country?"
            subtitle="Are you sure you want to publish this country?"
            itemName={selectedCountry.countryName}
            infoText="This country will be visible to the public once published."
            itemType="Member Country"
            isLoading={updateStatusMutation.isPending}
          />
          <UnpublishModal
            isOpen={unpublishModalOpen}
            onClose={() => { setUnpublishModalOpen(false); setSelectedCountry(null); }}
            onConfirm={handleConfirmStatusUpdate}
            itemType="Member Country"
            isLoading={updateStatusMutation.isPending}
          />
        </>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

// Geographic order for politically neutral display (North → East → West → Central → South)
const REGION_ORDER: Record<string, number> = {
  northern: 1, north: 1,
  eastern:  2, east:  2,
  western:  3, west:  3,
  central:  4,
  southern: 5, south: 5,
};

const getRegionOrder = (regionName: string): number => {
  const lower = regionName.toLowerCase();
  for (const [key, order] of Object.entries(REGION_ORDER)) {
    if (lower.includes(key)) return order;
  }
  return 99;
};

const RegionsList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());

  const { data: regionsData, isLoading } = useMemberCountryRegions();
  const { data: allCountriesData } = useAllMemberCountries();

  const countriesByRegion = useMemo(() => {
    const list: MemberCountry[] = Array.isArray(allCountriesData?.data) ? allCountriesData.data : [];
    return list.reduce<Record<string, MemberCountry[]>>((acc, country) => {
      (acc[country.regionId] ??= []).push(country);
      return acc;
    }, {});
  }, [allCountriesData]);

  const toggleRegion = (regionId: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
  };

  const filteredRegions = useMemo(() => {
    const regions = regionsData?.data ?? [];
    const filtered = Array.isArray(regions) && regions.length > 0 ? regions.filter((region: MemberCountryRegion) =>
      region.regionName.toLowerCase().includes(searchQuery.toLowerCase())
    ) : [];

    // Sort by geographic order: North → East → West → Central → South
    return filtered.sort((a: MemberCountryRegion, b: MemberCountryRegion) =>
      getRegionOrder(a.regionName) - getRegionOrder(b.regionName)
    );
  }, [regionsData, searchQuery]);

  return (
    <div className="space-y-4">

      {/* Member countries stats */}
      <MemberCountriesStats />

      {/* Search and Filter row */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:outline-none"
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Regions List */}
      <div>
        {isLoading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#047857]"></div>
            <p className="mt-4 text-sm text-gray-500">Loading regions...</p>
          </div>
        ) : filteredRegions.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-block bg-gray-200/20 p-4 rounded-full">
              <img src="/No-post-icon.png" alt="No regions" className="w-20 h-20 object-contain" />
            </div>
            <h3 className="mt-4 font-semibold text-gray-800 text-lg">No Regions</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'No regions match your search' : 'No regions available'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRegions.map((region: MemberCountryRegion) => (
              <RegionRow
                key={region.regionId}
                region={region}
                countries={countriesByRegion[region.regionId] ?? []}
                isExpanded={expandedRegions.has(region.regionId)}
                onToggle={() => toggleRegion(region.regionId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionsList;
